import React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../utils/class-names-util';

export type EnrollmentType = 'external' | 'modal';

interface UniversalEnrollButtonProps {
  enrollmentUrl?: string;
  enrollmentType?: EnrollmentType;
  variant?: 'primary' | 'secondary' | 'outline' | 'white-border';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  showIcon?: boolean;
  onModalOpen?: () => void; // For future modal implementation
  children?: React.ReactNode; // Allow custom button text
}

const UniversalEnrollButton: React.FC<UniversalEnrollButtonProps> = ({
  enrollmentUrl,
  enrollmentType = 'external',
  variant = 'primary',
  size = 'md',
  className,
  disabled = false,
  fullWidth = false,
  showIcon = true,
  onModalOpen,
  children
}) => {
  const { t } = useTranslation();

  const sizeClasses = {
    sm: 'px-3 py-1.5 text-xs',
    md: 'px-4 py-2.5 text-sm',
    lg: 'px-6 py-3 text-base'
  };

  const variantClasses = {
    primary: 'bg-black text-white hover:bg-gray-800 hover:shadow-lg',
    secondary: 'bg-white text-black border-2 border-black hover:bg-gray-50',
    outline: 'bg-transparent text-black border border-black hover:bg-black hover:text-white',
    'white-border': 'border-2 border-white text-white hover:bg-white hover:text-black'
  };

  const baseClasses = 'font-medium transition-all duration-300 rounded-lg flex items-center justify-center space-x-2 disabled:opacity-60 disabled:cursor-not-allowed';

  const handleEnroll = () => {
    if (disabled) return;

    if (enrollmentType === 'external' && enrollmentUrl) {
      window.open(enrollmentUrl, '_blank', 'noopener,noreferrer');
    } else if (enrollmentType === 'modal' && onModalOpen) {
      onModalOpen();
    }
  };

  const isClickable = (enrollmentType === 'external' && enrollmentUrl) || (enrollmentType === 'modal' && onModalOpen);

  return (
    <button
      onClick={handleEnroll}
      disabled={disabled || !isClickable}
      className={cn(
        baseClasses,
        sizeClasses[size],
        variantClasses[variant],
        fullWidth ? 'w-full' : '',
        className
      )}
    >
      {showIcon && (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
        </svg>
      )}
      <span>
        {children || t('enrollment.enrollNow', 'Enroll Now')}
      </span>
    </button>
  );
};

export default UniversalEnrollButton;